import { useModelLabel } from "@judo/i18n";
import type { EnumerationToggleButtonbar, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { FormControl, FormHelperText, InputLabel, ToggleButton, ToggleButtonGroup } from "@mui/material";

export interface EnumerationToggleButtonbarComponentProps {
	element: EnumerationToggleButtonbar | Input;
	readOnly?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
	options?: Array<{ value: string; label: string }>;
	/** Layout orientation of toggle buttons. @default "horizontal" */
	orientation?: "horizontal" | "vertical";
	/** When true, allows de-selection by clicking a selected toggle. @default false */
	nullable?: boolean;
}

/**
 * Enumeration toggle button bar component.
 * Renders a MUI ToggleButtonGroup with exclusive selection.
 */
export function EnumerationToggleButtonbarComponent({
	element,
	readOnly,
	value = "",
	onChange,
	error,
	disabled,
	options = [],
	orientation = "horizontal",
	nullable = false,
}: EnumerationToggleButtonbarComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const isDisabled = disabled ?? element.disabled;
	const isReadOnly = readOnly ?? element.isReadOnly;

	const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
		if (isReadOnly) return;
		if (newValue === null) {
			// Deselection: only allow if nullable
			if (nullable) {
				onChange?.("");
			}
		} else {
			onChange?.(newValue);
		}
	};

	return (
		<FormControl error={!!error} disabled={isDisabled} required={element.required}>
			<InputLabel shrink>{label}</InputLabel>
			<ToggleButtonGroup
				value={value || null}
				exclusive
				onChange={handleChange}
				orientation={orientation}
				data-testid={testId}
				disabled={isDisabled || isReadOnly}
				sx={{ mt: 1.25 }}
			>
				{options.map((option) => (
					<ToggleButton key={option.value} value={option.value}>
						{option.label}
					</ToggleButton>
				))}
			</ToggleButtonGroup>
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
}
